/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import axios from '../../utils/axios';
import { auth } from '../../firebase';

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ idToken }, { rejectWithValue }) => {
    try {
      const response = await axios.get('/us/users/login', {
        params: {
          idToken,
        },
      });

      if (response.status === 200 && response.data !== undefined) {
        const userInfo = response.data.user;
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        localStorage.setItem('teamId', response?.data?.organization.id);
        if (response?.data?.loginCount !== undefined) {
          localStorage.setItem('loginCount', String(response.data.loginCount));
        }
        return response.data;
      } else if (
        response.status === 200 &&
        response.data.isAuthenticated === false
      ) {
        const createUserResponse = await axios.post('/user/create', null, {
          params: {
            idToken,
            'request-id': uuidv4(),
          },
        });
        const token = await auth?.currentUser?.getIdToken(true);
        if (
          createUserResponse.status === 200 &&
          createUserResponse.data.status === 201
        ) {
          const verifyTokenResponse = await axios.get('/verifyToken', {
            params: {
              idToken: token,
            },
          });
          if (
            verifyTokenResponse.status === 200 &&
            verifyTokenResponse.data.data.isAuthenticated === true
          ) {
            const userInfo = verifyTokenResponse.data.data.user;
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
            return verifyTokenResponse.data.data;
          }
        } else if (
          createUserResponse.status === 200 &&
          createUserResponse.data.status === 403
        ) {
          return rejectWithValue(
            "You don't have an account with us. Please request a demo and get an invitation link for signup",
          );
        }
      }
      return rejectWithValue('Unable to signin');
    } catch (e) {
      return rejectWithValue('Unable to Login');
    }
  },
);

export const signupUser = createAsyncThunk(
  'auth/signupUser',
  async (
    {
      firstName,
      lastName,
      designations,
      profilePicture,
      idToken,
      invitationId,
      slug,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        '/us/users',
        {
          firstName,
          lastName,
          profilePicture: profilePicture
            ? {
                filename: profilePicture?.name,
                contentType: profilePicture?.type,
              }
            : null,
          designations: designations,
          authToken: idToken,
          slug,
        },
        {
          params: {
            invitationId: invitationId || null,
          },
          // Signup is the request that creates the account, so there is no
          // session to present: the credential is the `authToken` in the body
          // above. Without this the interceptor would attach whatever bearer
          // token Firebase happens to hold - on the invited-user flow the user
          // is already signed in by this point - and the API would be asked to
          // create a user while being told one is already authenticated.
          skipAuth: true,
        },
      );
      if (response.status === 200) {
        return response.data;
      }
      return rejectWithValue('Error creating account');
    } catch (e) {
      return rejectWithValue('Error creating account');
    }
  },
);

// Note: logoutUser needs to be careful about not introducing circular dependencies
// if it needed to import store/persistor. Since we can't import persistor here,
// the action logic requiring persistor will remain in the action wrapper/thunk in actions/Auth.
// This thunk purely handles the async signOut and local storage cleanup.
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      await signOut(auth);
      localStorage.removeItem('userInfo');
      localStorage.removeItem('teamId');
      localStorage.removeItem('isViewAsMode');
      return true;
    } catch (e) {
      return rejectWithValue('Logout failed');
    }
  },
);

export const checkSession = createAsyncThunk(
  'auth/checkSession',
  async (_, { dispatch }) => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        if (user && localStorage.getItem('userInfo')) {
          resolve(true);
        } else {
          dispatch(logoutUser());
          resolve(false);
        }
      });
    });
  },
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (idToken, { rejectWithValue }) => {
    try {
      const response = await axios.get('/us/users/verify', {
        params: {
          idToken,
        },
      });

      if (response.status === 200) {
        return response.data;
      }
      return rejectWithValue('Unable to check email verification');
    } catch (e) {
      return rejectWithValue('Unable to check email verification');
    }
  },
);

export const validateInvitation = createAsyncThunk(
  'auth/validateInvitation',
  async ({ invitationId, email }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/us/invitations/${invitationId}`, {
        params: {
          email,
        },
        skipAuth: true,
      });
      if (response.status === 200) {
        return response.data;
      }
      return rejectWithValue(
        response?.response?.data?.error || 'Link is invalid or expired.',
      );
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.error || 'Link is invalid or expired.',
      );
    }
  },
);

export const uploadUserPicture = createAsyncThunk(
  'auth/uploadUserPicture',
  async ({ payload, signedUrl, userId }, { rejectWithValue }) => {
    try {
      const gcpUploadResponse = await axios.put(signedUrl, payload, {
        headers: {
          'Content-Type': payload.type,
        },
      });
      if (gcpUploadResponse.status === 200) {
        const confirmUploadResponse = await axios.put(
          `/us/users/${userId}/profile-picture`,
          null,
          {},
        );
        if (confirmUploadResponse.status === 200) {
          return true;
        }
      }
      return rejectWithValue('Failed to upload picture');
    } catch (e) {
      return rejectWithValue('Failed to upload picture');
    }
  },
);

export const bookDemo = createAsyncThunk(
  'auth/bookDemo',
  async (requestDto, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/us/demo-request`, requestDto, {
        params: {
          'request-id': uuidv4(),
        },
      });
      if (response.status === 200) {
        return response.data;
      }
      return rejectWithValue('Failed to book the demo');
    } catch (e) {
      return rejectWithValue('Failed to book the demo');
    }
  },
);

export const sendResetPasswordLink = createAsyncThunk(
  'auth/sendResetPasswordLink',
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/us/users/reset-password`, email, {
        params: {
          'request-id': uuidv4(),
        },
        headers: {
          'Content-Type': 'plain/text',
        },
      });
      if (response.status === 200) {
        return response.data;
      }
      return rejectWithValue('Failed to send reset password email.');
    } catch (e) {
      return rejectWithValue('Failed to send reset password email.');
    }
  },
);

const initialState = {
  // `null` means "session not determined yet" -- it is NOT the same as `false`
  // ("checked, and logged out"). `useAppInit` holds the boot loader up while it
  // is `null`; with `false` as the initial value the boot gate released before
  // `checkSession()` had resolved. `checkSession` and `logoutUser` both write a
  // real boolean, so nothing downstream ever observes `null` after boot.
  isLoggedIn: null,
  checkingSession: false,
  currentUser: null,
  isViewAsMode: localStorage.getItem('isViewAsMode') === 'true',
  viewAsLoading: false,
  viewAsEmail: null,
  forceLogout: {
    show: false,
  },

  signin: {
    loading: false,
    success: false,
    error: null,
    data: null,
  },

  signup: {
    loading: false,
    success: false,
    error: null,
    data: null,
  },

  invitation: {
    validating: false,
    data: null,
    error: null,
  },

  emailVerification: {
    checking: false,
    success: false,
    error: null,
  },

  profilePicture: {
    uploading: false,
    success: false,
    error: null,
  },

  logout: {
    loading: false,
    success: false,
    error: null,
  },

  demoBooking: {
    loading: false,
    success: false,
    error: null,
  },

  resetPasswordLink: {
    sending: false,
    success: false,
    error: null,
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthState: () => initialState,
    setViewAsMode: (state, action) => {
      state.isViewAsMode = action.payload;
    },
    setViewAsLoading: (state, action) => {
      state.viewAsLoading = action.payload;
    },
    setViewAsEmail: (state, action) => {
      state.viewAsEmail = action.payload;
    },
    resetUIState: (state) => {
      // Reset non-session states?
      state.signin = initialState.signin;
      state.signup = initialState.signup;
      state.invitation = initialState.invitation;
      state.logout = initialState.logout;
      state.profilePicture = initialState.profilePicture;
      state.demoBooking = initialState.demoBooking;
      state.resetPasswordLink = initialState.resetPasswordLink;
    },
    resetDemoBooking: (state) => {
      state.demoBooking = initialState.demoBooking;
    },
    resetResetPasswordLink: (state) => {
      state.resetPasswordLink = initialState.resetPasswordLink;
    },
    setForceLogout: (state, action) => {
      state.forceLogout.show = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.signin.loading = true;
        state.signin.error = null;
        state.signin.success = false;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.signin.loading = false;
        state.signin.success = true;
        state.signin.data = action.payload;
        state.isLoggedIn = true;
        state.currentUser = action.payload.user;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.signin.loading = false;
        state.signin.error = action.payload;
        state.signin.success = false;
      });

    // Check Session
    builder
      .addCase(checkSession.pending, (state) => {
        state.checkingSession = true;
      })
      .addCase(checkSession.fulfilled, (state, action) => {
        state.checkingSession = false;
        state.isLoggedIn = action.payload;
      })
      .addCase(checkSession.rejected, (state) => {
        state.checkingSession = false;
        state.isLoggedIn = false;
      });

    // Signup
    builder
      .addCase(signupUser.pending, (state) => {
        state.signup.loading = true;
        state.signup.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.signup.loading = false;
        state.signup.success = true;
        state.signup.data = action.payload;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.signup.loading = false;
        state.signup.error = action.payload;
      });

    // Verify Email
    builder
      .addCase(verifyEmail.pending, (state) => {
        state.emailVerification.checking = true;
        state.emailVerification.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.emailVerification.checking = false;
        state.emailVerification.success = true;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.emailVerification.checking = false;
        state.emailVerification.error = action.payload;
      });

    // Validate Invitation
    builder
      .addCase(validateInvitation.pending, (state) => {
        state.invitation.validating = true;
        state.invitation.error = null;
      })
      .addCase(validateInvitation.fulfilled, (state, action) => {
        state.invitation.validating = false;
        state.invitation.data = action.payload;
      })
      .addCase(validateInvitation.rejected, (state, action) => {
        state.invitation.validating = false;
        state.invitation.error = action.payload;
      });

    // Upload Profile Picture
    builder
      .addCase(uploadUserPicture.pending, (state) => {
        state.profilePicture.uploading = true;
        state.profilePicture.error = null;
        state.profilePicture.success = false;
      })
      .addCase(uploadUserPicture.fulfilled, (state) => {
        state.profilePicture.uploading = false;
        state.profilePicture.success = true;
      })
      .addCase(uploadUserPicture.rejected, (state, action) => {
        state.profilePicture.uploading = false;
        state.profilePicture.error = action.payload;
      });

    // Logout
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.isLoggedIn = false;
      state.currentUser = null;
      state.signin = initialState.signin;
      state.logout.success = true;
      state.isViewAsMode = false;
    });

    // Book Demo
    builder
      .addCase(bookDemo.pending, (state) => {
        state.demoBooking.loading = true;
        state.demoBooking.error = null;
        state.demoBooking.success = false;
      })
      .addCase(bookDemo.fulfilled, (state) => {
        state.demoBooking.loading = false;
        state.demoBooking.success = true;
      })
      .addCase(bookDemo.rejected, (state, action) => {
        state.demoBooking.loading = false;
        state.demoBooking.error = action.payload;
      });

    // Send Reset Password Link
    builder
      .addCase(sendResetPasswordLink.pending, (state) => {
        state.resetPasswordLink.sending = true;
        state.resetPasswordLink.error = null;
        state.resetPasswordLink.success = false;
      })
      .addCase(sendResetPasswordLink.fulfilled, (state) => {
        state.resetPasswordLink.sending = false;
        state.resetPasswordLink.success = true;
      })
      .addCase(sendResetPasswordLink.rejected, (state, action) => {
        state.resetPasswordLink.sending = false;
        state.resetPasswordLink.error = action.payload;
      });
  },
});

export const {
  resetAuthState,
  setViewAsMode,
  setViewAsLoading,
  setViewAsEmail,
  resetUIState,
  resetDemoBooking,
  resetResetPasswordLink,
  setForceLogout,
} = authSlice.actions;
export default authSlice.reducer;
